import { type IconProps } from '@atlaskit/icon';
import { IconTile } from '@atlaskit/icon';
import HomeIcon from '@atlaskit/icon/core/home';
import ViewTypeBoardHomeIcon from '@atlaskit/icon-lab/core/view-type-board-home';
import ViewTypeCardHomeIcon from '@atlaskit/icon-lab/core/view-type-card-home';
import ViewTypeTableHomeIcon from '@atlaskit/icon-lab/core/view-type-table-home';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { messages } from '@databases/api-views/messages';
import { isNativeEmbedEditorPlugin } from '@databases/modes';
import React, { type ReactNode } from 'react';
import { useIntl } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

const unreachable = (value: string) => {
  throw new Error(`Not implemented: ${value}`);
};

const DefaultIconContainer = ({ children }: { children: ReactNode }) => {
  const { formatMessage } = useIntl();
  return (
    <Box
      xcss={xcss({
        position: 'relative',
        width: '24px',
        height: '24px',
      })}
      testId="default-view-icon"
    >
      {children}
      <Inline
        xcss={xcss({
          position: 'absolute',
          top: 'space.150',
          left: 'space.150',
          backgroundColor: 'elevation.surface',
          borderRadius: 'radius.full',
          width: '16px',
          height: '16px',
        })}
      >
        <IconTile
          size={'16'}
          appearance={'grayBold'}
          icon={HomeIcon}
          shape={'circle'}
          label={formatMessage(messages['views.default-view-icon-label'])}
          testId={'default-view-indicator-icon'}
        />
      </Inline>
    </Box>
  );
};

export interface LayoutIconProps {
  IconComponent: React.ComponentType<IconProps>;
  iconSize: 'small' | 'xlarge' | 'large' | 'medium';
  layoutType: { id: string; defaultMessage: string; description: string };
  isDefault: boolean;
  isPresentationOnly: boolean;
}

const LayoutIcon: React.FC<LayoutIconProps> = ({
  IconComponent,
  iconSize,
  layoutType,
  isDefault,
  isPresentationOnly,
}) => {
  const { formatMessage } = useIntl();

  const IconWrapper = ({ children }: { children: ReactNode }) => {
    const visualRefresh = isFeatureFlagEnabled('platform-visual-refresh-icons');
    if (isDefault && !isNativeEmbedEditorPlugin()) {
      if (visualRefresh) {
        switch (layoutType.defaultMessage) {
          case 'Gallery':
            return (
              <ViewTypeCardHomeIcon
                label={formatMessage(messages['views.default-view-icon-label'])}
                testId="default-view-icon"
              />
            );
          case 'Board':
            return (
              <ViewTypeBoardHomeIcon
                label={formatMessage(messages['views.default-view-icon-label'])}
                testId="default-view-icon"
              />
            );
          case 'Table':
            return (
              <ViewTypeTableHomeIcon
                label={formatMessage(messages['views.default-view-icon-label'])}
                testId="default-view-icon"
              />
            );
          case 'Calendar':
            return null;
          default:
            unreachable(layoutType.defaultMessage);
        }
      }
      return <DefaultIconContainer>{children}</DefaultIconContainer>;
    }
    return <>{children}</>;
  };

  const isIconTile = iconSize == 'large' || iconSize == 'xlarge';

  return (
    <>
      {isIconTile ? (
        <IconTile
          appearance={'gray'}
          icon={IconComponent}
          label={
            !isPresentationOnly
              ? formatMessage(messages['view-settings.layout.layout-text'], {
                  layoutType: formatMessage(layoutType),
                })
              : ''
          }
        />
      ) : (
        <IconWrapper>
          <IconComponent
            size={iconSize}
            label={
              !isPresentationOnly
                ? formatMessage(messages['view-settings.layout.layout-text'], {
                    layoutType: formatMessage(layoutType),
                  })
                : ''
            }
          />
        </IconWrapper>
      )}
    </>
  );
};

export default LayoutIcon;
